export interface AuthUser {
  access_tier: "standard" | "unlimited";
  avatar_url: string | null;
  email: string;
  user_id: string;
  username: string;
}

export interface AuthSession {
  access_token: string;
  token_type: "bearer";
  user: AuthUser;
}

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "/api/v1").replace(/\/$/, "");

export type AuthRequestStage = "csrf" | "refresh";
export type AuthRequestKind = "unauthenticated" | "transient";

export class AuthRequestError extends Error {
  constructor(
    public readonly status: number | null,
    public readonly stage: AuthRequestStage,
    public readonly kind: AuthRequestKind,
    message: string,
  ) {
    super(message);
    this.name = "AuthRequestError";
  }
}

async function request<T>(path: string, init: RequestInit = {}, stage: AuthRequestStage): Promise<T | undefined> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, { ...init, credentials: "include" });
  } catch {
    throw new AuthRequestError(null, stage, "transient", `Authentication ${stage} request failed`);
  }
  if (!response.ok) {
    const kind = response.status === 401 ? "unauthenticated" : "transient";
    throw new AuthRequestError(response.status, stage, kind, `Authentication request failed (${response.status})`);
  }
  if (response.status === 204) return undefined;
  try {
    return await response.json() as T;
  } catch {
    throw new AuthRequestError(null, stage, "transient", `Authentication ${stage} response was invalid`);
  }
}

export async function getCsrfToken(): Promise<string | null> {
  const result = await request<{ csrf_token?: unknown }>("/auth/csrf", {}, "csrf");
  return typeof result?.csrf_token === "string" && result.csrf_token ? result.csrf_token : null;
}

export async function refreshAuthSession(): Promise<AuthSession> {
  const csrf = await getCsrfToken();
  if (!csrf) throw new AuthRequestError(204, "csrf", "unauthenticated", "Authentication is unavailable");
  return await request<AuthSession>("/auth/refresh", {
    method: "POST",
    headers: { "X-CSRF-Token": csrf },
  }, "refresh") as AuthSession;
}
